import { error, warn } from '@jepto/shared/utils/logger';

import { BaseStreamingComponent } from './base-streaming-component';

export interface ToolResultData {
	toolName?: string;
	summary?: string;
	data?: unknown;
	status?: 'success' | 'error' | 'loading';
	duration?: number;
	meta?: {
		request?: unknown;
		response?: unknown;
	};
}

interface ContentSection {
	label: string;
	body: string;
}

const ICON_PLACEHOLDER = '▾';

export class DatachatToolResultComponent extends BaseStreamingComponent {
	private static instanceCounter = 0;
	private readonly instanceId: number;
	private isExpanded = true;
	private toolData: ToolResultData = {};

	constructor() {
		super();
		this.instanceId = DatachatToolResultComponent.instanceCounter += 1;
	}

	static get observedAttributes() {
		return ['data'];
	}

	attributeChangedCallback(
		name: string,
		_oldValue: string | null,
		newValue: string | null
	) {
		if (name === 'data' && newValue != null) {
			try {
				this.toolData = JSON.parse(newValue);
				if (this.isConnected) {
					this.render();
				}
			} catch (e) {
				error('Invalid tool result data:', newValue, e);
			}
		}
	}

	connectedCallback() {
		if (!this.shadowRoot) {
			try {
				this.attachShadow({ mode: 'open' });
			} catch (attachError) {
				warn(
					'Could not attach shadow DOM, using regular DOM:',
					attachError
				);
			}
		}
		this.render();
	}

	private render(): void {
		const targetElement = this.shadowRoot || this;
		const { toolName, summary, status, duration } = this.toolData;
		const headerId = `tool-result-header-${this.instanceId}`;
		const contentId = `tool-result-content-${this.instanceId}`;

		targetElement.innerHTML = `
			<style>
				:host {
					display: block;
					font-family: 'Proxima Nova', -apple-system, BlinkMacSystemFont, 'Segoe UI',
						Roboto, sans-serif;
					color: #0f1526d1;
				}

				.accordion {
					border: 1px solid rgba(15, 21, 38, 0.12);
					border-radius: 12px;
					background: #f6f7fb;
					overflow: hidden;
					transition: border-color 0.2s ease;
				}

				.accordion-header {
					width: 100%;
					border: none;
					background: transparent;
					padding: 12px 16px;
					display: flex;
					align-items: center;
					gap: 12px;
					cursor: pointer;
					text-align: left;
					font: inherit;
					color: inherit;
				}

				.accordion-header:focus-visible {
					outline: 2px solid #6c5ce7;
					outline-offset: 2px;
				}

				.header-left {
					display: flex;
					align-items: center;
					gap: 12px;
					flex: 1;
				}

				.chevron {
					width: 16px;
					height: 16px;
					display: inline-flex;
					align-items: center;
					justify-content: center;
					transform: rotate(${this.isExpanded ? '180deg' : '0deg'});
					transition: transform 0.2s ease;
					font-family: 'jepto-icon', 'jepto-icon:Regular', sans-serif;
					font-size: 16px;
					color: rgba(15, 21, 38, 0.54);
				}

				.tool-icon {
					width: 32px;
					height: 32px;
					border-radius: 8px;
					background: linear-gradient(135deg, #a855f7, #6366f1);
					display: flex;
					align-items: center;
					justify-content: center;
					font-size: 16px;
					color: #ffffff;
					font-weight: 600;
					text-transform: uppercase;
				}

				.tool-title {
					display: flex;
					flex-direction: column;
					gap: 2px;
				}

				.tool-name {
					font-size: 13px;
					line-height: 22px;
					color: rgba(15, 21, 38, 0.82);
					font-weight: 600;
				}

				.tool-summary {
					font-size: 12px;
					line-height: 18px;
					color: rgba(15, 21, 38, 0.64);
				}

				.header-meta {
					display: flex;
					align-items: center;
					gap: 8px;
				}

				.status-badge {
					border-radius: 24px;
					padding: 2px 10px;
					font-size: 11px;
					font-weight: 600;
					text-transform: uppercase;
					letter-spacing: 0.4px;
				}

				.status-success {
					background: rgba(76, 217, 100, 0.16);
					color: #1b7f3d;
				}

				.status-error {
					background: rgba(255, 82, 82, 0.18);
					color: #c53030;
				}

				.status-loading {
					background: rgba(99, 102, 241, 0.15);
					color: #4f46e5;
				}

				.duration {
					font-size: 11px;
					color: rgba(15, 21, 38, 0.54);
				}

				.spinner {
					width: 16px;
					height: 16px;
					border-radius: 50%;
					border: 2px solid rgba(99, 102, 241, 0.2);
					border-top-color: #6366f1;
					animation: spin 0.9s linear infinite;
				}

				@keyframes spin {
					to {
						transform: rotate(360deg);
					}
				}

				.accordion-content {
					max-height: ${this.isExpanded ? '800px' : '0'};
					overflow: auto;
					transition: max-height 0.28s ease;
				}

				.content-inner {
					padding: 12px 16px 16px;
					display: flex;
					flex-direction: column;
					gap: 12px;
					background: #ffffff;
				}

				.section {
					border: 1px solid rgba(15, 21, 38, 0.12);
					border-radius: 8px;
					padding: 12px 16px;
					background: #ffffff;
					color: rgba(15, 21, 38, 0.82);
				}

				.section-label {
					font-size: 11px;
					font-weight: 500;
					text-transform: uppercase;
					letter-spacing: 0.6px;
					color: rgba(15, 21, 38, 0.64);
					margin-bottom: 8px;
				}

				.section-body {
					font-size: 12px;
					line-height: 20px;
					font-family: 'Roboto Mono', 'SFMono-Regular', Menlo, Monaco,
						Consolas, 'Liberation Mono', 'Courier New', monospace;
					white-space: pre-wrap;
				}

				.json-block {
					margin: 0;
					white-space: pre-wrap;
				}

				.json-key {
					color: #f3403d;
				}

				.json-string {
					color: #66bb6a;
				}

				.json-number {
					color: #42a5f5;
				}

				.json-boolean {
					color: #651fff;
				}

				.json-null {
					color: #9c27b0;
				}

				.empty-state {
					padding: 16px;
					text-align: center;
					font-style: italic;
					color: rgba(15, 21, 38, 0.54);
				}
			</style>

			<div class="accordion ${this.isExpanded ? 'expanded' : ''}">
				<button
					class="accordion-header"
					data-action="toggle"
					id="${headerId}"
					aria-controls="${contentId}"
					aria-expanded="${this.isExpanded}"
				>
					<span class="header-left">
						<span class="chevron" aria-hidden="true">${ICON_PLACEHOLDER}</span>
						<span class="tool-title">
							<span class="tool-name">${this.escapeHtml(this.getDisplayName(toolName))}</span>
							${summary ? `<span class="tool-summary">${summary}</span>` : ''}
						</span>
					</span>
					<span class="header-meta">
						${this.renderStatusBadge(status)}
						${duration ? `<span class="duration">${this.escapeHtml(String(duration))} ms</span>` : ''}
						${this.isStreaming ? '<span class="spinner" role="status" aria-label="Streaming"></span>' : ''}
					</span>
				</button>
				<div
					class="accordion-content"
					id="${contentId}"
					role="region"
					aria-labelledby="${headerId}"
				>
					<div class="content-inner">
						${this.renderSections()}
					</div>
				</div>
			</div>
		`;

		this.setupEventListeners();
	}

	private setupEventListeners(): void {
		const targetElement = this.shadowRoot || this;
		const header = targetElement.querySelector(
			'[data-action="toggle"]'
		) as HTMLElement | null;
		if (!header) {
			return;
		}
		header.removeEventListener(
			'click',
			this.handleToggleClick as EventListener
		);
		header.removeEventListener(
			'keydown',
			this.handleToggleKeydown as EventListener
		);
		header.addEventListener(
			'click',
			this.handleToggleClick as EventListener
		);
		header.addEventListener(
			'keydown',
			this.handleToggleKeydown as EventListener
		);
	}

	private handleToggleClick = () => {
		this.toggle();
	};

	private handleToggleKeydown = (event: KeyboardEvent) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			this.toggle();
		}
	};

	private toggle(): void {
		this.isExpanded = !this.isExpanded;
		this.render();
	}

	private renderSections(): string {
		const sections = this.buildSections();
		if (!sections.length) {
			return '<div class="empty-state">No data available</div>';
		}

		return sections
			.map(
				section => `
					<div class="section">
						<div class="section-label">${this.escapeHtml(section.label)}</div>
						<div class="section-body">${section.body}</div>
					</div>
				`
			)
			.join('');
	}

	private buildSections(): ContentSection[] {
		const sections: ContentSection[] = [];
		const { status, data, meta } = this.toolData;

		if (meta?.request !== undefined) {
			sections.push({
				label: 'Request',
				body: this.renderValue(meta.request),
			});
		}

		if (meta?.response !== undefined) {
			sections.push({
				label: 'Response',
				body: this.renderValue(meta.response),
			});
		}

		if (sections.length) {
			return sections;
		}

		if (status === 'loading') {
			sections.push({
				label: 'Request',
				body: this.renderValue(data),
			});
			return sections;
		}

		if (this.isPlainObject(data)) {
			const objectData = data as Record<string, unknown>;
			const keys = Object.keys(objectData);
			for (const key of keys) {
				const value = objectData[key];
				sections.push({
					label: this.toTitleCase(key),
					body: this.renderValue(value),
				});
			}
			return sections;
		}

		if (data !== undefined && data !== null) {
			sections.push({
				label: status === 'error' ? 'Error' : 'Response',
				body: this.renderValue(data),
			});
		}

		return sections;
	}

	private renderValue(value: unknown): string {
		if (value === null || value === undefined) {
			return '<span class="json-null">null</span>';
		}

		if (typeof value === 'string') {
			const trimmed = value.trim();
			if (this.isJsonString(trimmed)) {
				try {
					return this.renderJson(JSON.parse(trimmed));
				} catch {
					return `<span class="json-block">${this.escapeHtml(trimmed)}</span>`;
				}
			}
			return `<span>${this.escapeHtml(value)}</span>`;
		}

		if (typeof value === 'number' || typeof value === 'boolean') {
			return `<span>${this.escapeHtml(String(value))}</span>`;
		}

		if (this.isPlainObject(value) || Array.isArray(value)) {
			return this.renderJson(value);
		}

		return `<span>${this.escapeHtml(String(value))}</span>`;
	}

	private renderJson(value: unknown): string {
		try {
			const json = JSON.stringify(value, null, 2);
			const highlighted = this.escapeHtml(json)
				.replace(
					/(&quot;.*?&quot;)(\s*:\s*)/g,
					'<span class="json-key">$1</span><span>$2</span>'
				)
				.replace(
					/:\s*(-?\d+(?:\.\d+)?)/g,
					': <span class="json-number">$1</span>'
				)
				.replace(
					/:\s*&quot;([^\n]*?)&quot;/g,
					': <span class="json-string">&quot;$1&quot;</span>'
				)
				.replace(
					/:\s*(true|false)/g,
					': <span class="json-boolean">$1</span>'
				)
				.replace(/:\s*(null)/g, ': <span class="json-null">$1</span>');

			return `<pre class="json-block">${highlighted}</pre>`;
		} catch {
			return `<pre class="json-block">${this.escapeHtml(String(value))}</pre>`;
		}
	}

	private renderStatusBadge(status?: string): string {
		if (!status) {
			return '';
		}

		const statusMap: Record<string, string> = {
			success: '<span class="status-badge status-success">Success</span>',
			error: '<span class="status-badge status-error">Error</span>',
			loading: '<span class="status-badge status-loading">Running</span>',
		};

		return statusMap[status] || '';
	}

	private getToolInitials(toolName?: string): string {
		if (!toolName) {
			return 'AI';
		}

		const sanitized = toolName
			.replace(/[^a-zA-Z0-9 ]+/g, ' ')
			.trim()
			.split(/\s+/)
			.map(segment => segment[0]?.toUpperCase())
			.join('');

		return sanitized.slice(0, 2) || 'AI';
	}

	private getDisplayName(toolName?: string): string {
		if (!toolName) {
			return 'Tool Execution';
		}

		return toolName
			.replace(/[_-]+/g, ' ')
			.replace(/\b\w/g, match => match.toUpperCase());
	}

	private escapeHtml(value: string): string {
		return value
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	}

	private isJsonString(value: string): boolean {
		if (!value) {
			return false;
		}

		const startsWithBrace = value.startsWith('{') || value.startsWith('[');
		const endsWithBrace = value.endsWith('}') || value.endsWith(']');
		return startsWithBrace && endsWithBrace;
	}

	private isPlainObject(value: unknown): value is Record<string, unknown> {
		return (
			typeof value === 'object' &&
			value !== null &&
			Object.getPrototypeOf(value) === Object.prototype
		);
	}

	private toTitleCase(value: string): string {
		return value
			.replace(/[_-]+/g, ' ')
			.replace(/\b\w/g, match => match.toUpperCase());
	}

	protected updateContent(): void {
		if (this.streamingBuffer) {
			try {
				const partialData = JSON.parse(this.streamingBuffer);
				this.toolData = { ...this.toolData, ...partialData };
				this.streamingBuffer = '';
				this.render();
			} catch {
				// Data is still incomplete, keep buffering
			}
		}
	}

	protected updateStreamingIndicator(): void {
		this.render();
	}
}
